import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import Step from "@material-ui/core/Step";
import StepButton from "@material-ui/core/StepButton";
import StepLabel from "@material-ui/core/StepLabel";
import Stepper from "@material-ui/core/Stepper";
import React, { FC, useCallback, useState } from "react";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";

import BaseContent from "../BaseContent";
import Button from "../../elements/Button";
import StepIcon from "./StepIcon";
import classes from "./BaseStepper.module.scss";
import { BaseStepperStyleConnector } from "./styled";
import { CAMPAIGN_STEPS, CampaignType } from "../../../types/campaignTypes";
import { ReduxApplicationState } from "../../../types/ReduxTypes/ReduxApplicationState";

export type BaseStepComponentProps = {
  isAttemptingToGoNext: boolean;
  setIsAttemptingToGoNext: (isAttemptingToGoNext: boolean) => void;
  handleNext: () => void;
  campaignAction: (
    onCampaignCreated: (createdCampaign: CampaignType) => void,
  ) => ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction>;
  typeOfAction?: "create" | "update";
  handleResetSteps?: () => void;
  setIsNextButtonDisabled?: (isNextButtonDisabled: boolean) => void;
};

export type PageStepComponentProps = {
  isAttemptingToGoNext?: boolean;
  setIsAttemptingToGoNext?: (isAttemptingToGoNext: boolean) => void;
  handleNext?: () => void;
  handleResetSteps?: () => void;
  setIsNextButtonDisabled?: (isNextButtonDisabled: boolean) => void;
};

export type BaseStep = {
  order?: number;
  label: string;
  name: CAMPAIGN_STEPS;
  stepComponent: (props: BaseStepComponentProps) => React.ReactNode;
};

type BaseStepperProps = {
  steps: BaseStep[];
  nonLinear?: boolean;
  lastStepNextLabel?: string;
  handleBack: () => void;
  campaignAction: (
    onCampaignCreated: (createdCampaign: CampaignType) => void,
  ) => ThunkAction<Promise<void>, ReduxApplicationState, {}, AnyAction>;
  typeOfAction?: "create" | "update";
};

const BaseStepper: FC<BaseStepperProps> = ({
  steps,
  nonLinear,
  handleBack,
  lastStepNextLabel,
  campaignAction,
  typeOfAction,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isAttemptingToGoNext, setIsAttemptingToGoNext] = useState(false);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
  const [completedSteps, setCompletedSteps] = useState<{ [k: number]: boolean }>({});

  const handleNext = useCallback(() => {
    setActiveStep((prevActiveStep) => {
      setCompletedSteps({
        ...completedSteps,
        [prevActiveStep]: true,
      });
      return prevActiveStep + 1;
    });
  }, [completedSteps]);

  const handleAttemptingToGoNext = () => {
    setIsAttemptingToGoNext(true);
  };

  const handlePrev = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setIsAttemptingToGoNext(false);
  };

  const handleStepClick = (step: number) => () => {
    if (completedSteps[step] || completedSteps[step - 1]) {
      setActiveStep(step);
    }
  };

  const handleResetSteps = () => {
    setActiveStep(0);
    setCompletedSteps({});
  };

  return (
    <>
      <div className={classes.stepperContainer}>
        <Stepper nonLinear={nonLinear} activeStep={activeStep} connector={<BaseStepperStyleConnector />}>
          {steps
            .sort((a, b) => a?.order! - b?.order!)
            .map((step: BaseStep, index: number) => {
              return (
                <Step key={step.label} completed={completedSteps[index]}>
                  {nonLinear ? (
                    <StepButton onClick={handleStepClick(index)}>
                      <StepLabel StepIconComponent={StepIcon}>{step.label}</StepLabel>
                    </StepButton>
                  ) : (
                    <StepLabel StepIconComponent={StepIcon}>{step.label}</StepLabel>
                  )}
                </Step>
              );
            })}
        </Stepper>
      </div>
      <BaseContent>
        {steps[activeStep].stepComponent({
          isAttemptingToGoNext,
          setIsAttemptingToGoNext,
          setIsNextButtonDisabled,
          handleNext,
          handleResetSteps,
          campaignAction,
          typeOfAction,
        })}
      </BaseContent>
      <div className={classes.stepperControls}>
        {activeStep === 0 ? (
          <Button variant="outlined" onClick={handleBack}>
            Back
          </Button>
        ) : (
          <Button variant="outlined" disabled={activeStep === 0} onClick={handlePrev}>
            <KeyboardArrowLeftIcon />
            Previous
          </Button>
        )}

        <Button onClick={handleAttemptingToGoNext} disabled={isNextButtonDisabled}>
          {activeStep === steps.length - 1 && lastStepNextLabel ? lastStepNextLabel : "Next"}
          <KeyboardArrowRightIcon />
        </Button>
      </div>
    </>
  );
};

export default BaseStepper;
